import { useEffect, useMemo, useState, useRef } from "react"
import { BASEURI, getAuthToken, handleAuthTokenError } from "../../utils"

function toUtcMs(dateStr, timeStr) {
    // fechaMedicion: "YYYY-MM-DD", horaMedicion: "HH:mm:ss"
    // Treat as UTC to align with backend ISO timestamps
    const iso = `${dateStr}T${timeStr}Z`
    const ms = Date.parse(iso)
    return Number.isFinite(ms) ? ms : null
}

// Support both signatures:
// 1) useSubmissionsTimeseries(selectedJob, { startTs, endTs, limit })
// 2) useSubmissionsTimeseries({ id, start_ts|startTs, end_ts|endTs, limit })
export default function useSubmissionsTimeseries(selectedJob, opts, refreshToken) {
    const [series, setSeries] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const abortRef = useRef(null)

    // Normalize inputs
    const { jobId, startIso, endIso, limit } = useMemo(() => {
        // job id can come from first arg (job object) or config
        const id = selectedJob?.id

        const startInput = opts.startTs
        const endInput = opts.endTs
        const end = endInput ? new Date(endInput) : new Date()
        const start = startInput ? new Date(startInput) : new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000)

        return {
            jobId: id,
            startIso: start.toISOString(),
            endIso: end.toISOString(),
            limit: (opts.limit ?? 8640)
        }
    }, [selectedJob, opts])

    useEffect(() => {
        if (!jobId) {
            setSeries([])
            setLoading(false)
            setError(null)
            return
        }

        // abort previous in-flight request
        if (abortRef.current) abortRef.current.abort()
        const ac = new AbortController()
        abortRef.current = ac

        const fetchData = async () => {
            setLoading(true)
            setError(null)
            try {
                const params = new URLSearchParams({
                    job_id: String(jobId),
                    start_ts: startIso,
                    end_ts: endIso,
                    limit: String(limit),
                })
                const url = `${BASEURI}/api/dga/submissions/?${params.toString()}`

                const token = await getAuthToken().catch(handleAuthTokenError)
                const res = await fetch(url, {
                    method: 'GET',
                    headers: {
                        "Content-Type": "application/json; charset=utf-8",
                        ...(token ? { Authorization: `Bearer  ${token}` } : {}),
                    },
                    signal: ac.signal,
                })
                if (!res.ok) {
                    const msg = await res.text().catch(() => res.statusText)
                    throw new Error(msg || `Failed to load submissions (${res.status})`)
                }
                const data = await res.json()
                const items = Array.isArray(data?.items) ? data.items : []

                const totalizer = []
                const flow = []
                const level = []

                for (const it of items) {
                    const ts = toUtcMs(it.fechaMedicion, it.horaMedicion)
                    if (!ts) continue
                    const totalizadorM3 = it.totalizadorM3 != null ? parseFloat(it.totalizadorM3) : null
                    const caudalLps = it.caudalLps != null ? parseFloat(it.caudalLps) : null
                    const nivelFreaticoM = it.nivelFreaticoM != null ? parseFloat(it.nivelFreaticoM) : null

                    if (Number.isFinite(totalizadorM3)) totalizer.push([ts, totalizadorM3])
                    if (Number.isFinite(caudalLps)) flow.push([ts, caudalLps])
                    if (Number.isFinite(nivelFreaticoM)) level.push([ts, nivelFreaticoM])
                }

                totalizer.sort((a, b) => a[0] - b[0])
                flow.sort((a, b) => a[0] - b[0])
                level.sort((a, b) => a[0] - b[0])

                const nextSeries = [
                    {
                        calculation: 'Forecast',
                        label: "Totalizer (m³)",
                        y_axis_legend: "m³",
                        data: totalizer,
                        _type: 'line',
                        axis_min: null,
                        axis_max: null,
                        color: "#10b981",
                    },
                    {
                        calculation: 'Forecast',
                        label: "Well level (m)",
                        y_axis_legend: "m",
                        data: level,
                        _type: "line",
                        axis_min: null,
                        axis_max: null,
                        color: "#3b82f6",
                    },
                    {
                        calculation: 'Forecast',
                        label: "Flow (L/s)",
                        y_axis_legend: "L/s",
                        data: flow,
                        _type: "line",
                        axis_min: null,
                        axis_max: null,
                        color: "#8b5cf6",
                    },
                ]
                setSeries(nextSeries)
            } catch (e) {
                if (ac.signal.aborted) return
                setError(e)
                setSeries([])
            } finally {
                if (!ac.signal.aborted) setLoading(false)
            }
        }

        fetchData()
        return () => ac.abort()
    }, [jobId, startIso, endIso, limit, refreshToken])

    return { series, loading, error }
}
